import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { Button } from '@repo/ui';
import { convertNumberToLocaleMoneyFormat } from '@repo/shared/helpers';
import { Limit, LimitMerchant, OfferLimit } from '@repo/shared/types';
import { useIsMobile } from '@repo/mui-config/hooks';
import { colorPalette } from '@repo/mui-config/color-palette';

import { LoanProcessStatusIconWrapper } from '@/components/loan-process/loan-process-status-icon-wrapper';
import { useLoanProcessSetCurrentModalState } from '@/components/loan-process/hooks/use-loan-process-set-current-modal-state';
import { useLoanProcessModal } from '@/components/loan-process/hooks/use-loan-process-modal';
import { LoanProcessModalHeader } from '@/components/loan-process/loan-process-modal/loan-process-modal-header';
import type { LoanStoreData } from '@/components/loan-process/store/loan-process.store';
import { useLoanProcessStore } from '@/components/loan-process/store/loan-process.store';
import { VIEW_TYPE } from '@/store/view.type';
import { useMainHubStoreActions } from '@/store/main-hub.store';
import { MODAL_STATE, useModalStore } from '@/components/modal/modal.store';

interface ApplicationChangedLoanAmount {
  loanLimit: Limit | LimitMerchant | OfferLimit;
  loanData: LoanStoreData;
}

export function ApplicationChangedLoanAmount({
  loanLimit,
  loanData,
}: ApplicationChangedLoanAmount) {
  const { openModal, closeModal } = useModalStore();
  const isMobile = useIsMobile();

  useLoanProcessSetCurrentModalState('ApplicationChangedLoanAmount');
  const { t } = useTranslation();
  const { setLoanStage } = useLoanProcessModal();

  const {
    actions: { clearLoanProcessStore },
  } = useLoanProcessStore();
  const { setCurrentView } = useMainHubStoreActions();

  const handleOnCancelSuccess = () => {
    clearLoanProcessStore();
    setCurrentView(VIEW_TYPE.DASHBOARD);
  };

  const handleCancelLoan = () => {
    openModal(MODAL_STATE.LOAN_CANCEL, undefined, {
      handleOnCancelSuccess,
      handleFinishCurrentApplication: closeModal,
      loanData,
    });
  };

  return (
    <Box data-testid="application-positive" sx={{ px: isMobile ? 2 : 4, py: 4 }}>
      <LoanProcessModalHeader containerSx={{ mb: isMobile ? 4 : 8 }} />
      <Stack
        alignContent="center"
        alignItems="center"
        gap={7}
        sx={{ marginBottom: '64px', paddingX: '0px' }}
      >
        <Typography component="p" textAlign="center" variant="h5">
          {t('loanProcess.loanSteps.application.loanAmountChange.title')}
        </Typography>
        <LoanProcessStatusIconWrapper iconType="success">
          <CheckIcon />
        </LoanProcessStatusIconWrapper>

        <Box>
          <Stack>
            <Typography textAlign="center">
              {t('loanProcess.loanSteps.application.loanAmountChange.newLimitDescription')}
            </Typography>
            <Typography textAlign="center">
              {convertNumberToLocaleMoneyFormat(loanLimit.max)} {loanLimit.currency}
            </Typography>
          </Stack>
          <Stack mt={2}>
            <Typography textAlign="center">
              {t('loanProcess.loanSteps.application.loanAmountChange.possibleAmount')}
            </Typography>
            <Typography textAlign="center" variant="h5">
              {convertNumberToLocaleMoneyFormat(loanData.amount)} {loanLimit.currency}
            </Typography>
            <Typography textAlign="center">
              {t('loanProcess.loanSteps.application.loanAmountChange.doYouWantContinue')}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <>
        <Button
          color="secondary"
          data-testid="application-positive-button"
          fullWidth
          onClick={() => {
            setLoanStage(2, {
              viewOptions: {
                stageViewType: 'AGREEMENT_AND_APPROVALS',
                AGREEMENT_AND_APPROVALS: {
                  openInEditMode: false,
                },
              },
            });
          }}
          sx={{ paddingY: { md: '14px', xs: '8px' } }}
          variant="contained"
        >
          {t('loanProcess.loanSteps.application.loanAmountChange.button.continue')}
        </Button>
        <Button
          data-testid="application-positive-button"
          fullWidth
          onClick={handleCancelLoan}
          sx={{
            paddingY: { md: '14px', xs: '8px' },
            color: colorPalette.pale.main,
          }}
          variant="text"
        >
          {t('loanProcess.loanSteps.application.loanAmountChange.button.cancel')}
        </Button>
      </>
    </Box>
  );
}
